import {
  ChangeEventHandler,
  FunctionComponent,
  KeyboardEventHandler,
  useEffect,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import { DraftConfigurationWithTextOrSpecialText } from '@/types';
import { trackFeature } from '@/utils/adobeLaunch';
import { t } from '@/utils/i18n';
import { mmToPt } from '@/utils/math/mmToPt';
import { ptToMm } from '@/utils/math/ptToMm';
import { getMinFontSize } from '@/utils/text/getMinFontSize';
import { getCurrentSizeOfConfiguration } from '@/model/configuration/getCurrentSizeOfConfiguration';
import { getMaxScaleForConfiguration } from '@/model/configuration/getMaxScale';
import { getOffsetAfterResize } from '@/model/configuration/getOffsetAfterResize';
import {
  isDraftBendingTextConfiguration,
  isDraftSpecialTextConfiguration,
  isDraftTextOrPlaceHolderTextConfiguration,
} from '@/model/configuration/isDraftConfigurationType';
import { scaleSize } from '@/model/configuration/scaleSize';
import { setConfigurationTextStyle } from '@/redux/draft/actions/setConfigurationTextStyle';
import { useReduxDraftDispatch } from '@/redux/draft/draftDispatch';
import { StoreDispatch } from '@/redux/types';
import { Minus } from '@/components/common/icons/Minus';
import { Plus } from '@/components/common/icons/Plus';
import { setTransformationAction } from '@/components/draft/draftState/actions/setTransformationAction';
import './FontSize.scss';

const getFontSize = (configuration: DraftConfigurationWithTextOrSpecialText) => {
  if (isDraftTextOrPlaceHolderTextConfiguration(configuration))
    return mmToPt(configuration.text.style.fontSize * configuration.scale);
  if (isDraftBendingTextConfiguration(configuration)) {
    const maxScale = getMaxScaleForConfiguration(configuration);
    return Math.round((configuration.scale * 100) / maxScale);
  }
  return 0;
};

interface Props {
  configuration: DraftConfigurationWithTextOrSpecialText;
}

interface FontSizeInputProps {
  inputValue?: string;
  disabled?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler;
  onBlur?: () => void;
  onDecrease?: () => void;
  onIncrease?: () => void;
}

const FontSizeInput: FunctionComponent<FontSizeInputProps> = ({
  inputValue = '0',
  disabled,
  onChange,
  onKeyDown,
  onBlur,
  onDecrease,
  onIncrease,
}) => (
  <div
    className={cn('font-size', {
      'font-size--disabled': disabled,
    })}
  >
    <label className="font-size__label" htmlFor="font-size-input">
      {t('configuration.text.fontSize')}
    </label>
    <div className="font-size__selector">
      <button
        type="button"
        title={t('textSettings.decrease', String(Number(inputValue) - 1))}
        className="font-size__button"
        onClick={onDecrease}
      >
        <Minus />
      </button>
      <input
        id="font-size-input"
        className="font-size__input"
        type="text"
        value={inputValue}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        pattern="\d*"
        readOnly={disabled}
      />
      <button
        type="button"
        title={t('textSettings.increase', String(Number(inputValue) + 1))}
        className="font-size__button"
        onClick={onIncrease}
      >
        <Plus />
      </button>
    </div>
  </div>
);

const asIntegerString = (number: number) => number.toFixed(0);

export const FontSize: FunctionComponent<Props> = ({ configuration }) => {
  const dispatch = useDispatch<StoreDispatch>();
  const draftDispatch = useReduxDraftDispatch();
  const fontSize = getFontSize(configuration);
  const [inputValue, setInputValue] = useState(asIntegerString(fontSize));
  useEffect(() => {
    setInputValue(asIntegerString(fontSize));
  }, [fontSize]);

  if (isDraftSpecialTextConfiguration(configuration)) {
    return <FontSizeInput disabled />;
  }

  const scale = configuration.scale;
  const textStyle = configuration.text.style;
  const font = textStyle.font;

  const minFontSize = Math.ceil(mmToPt(getMinFontSize(configuration.printType, font)));

  const onChange = (fontSize: number) => {
    trackFeature('text-size');
    if (isDraftBendingTextConfiguration(configuration)) {
      const maxScale = getMaxScaleForConfiguration(configuration);
      const nextScale = (fontSize * maxScale) / 100;
      draftDispatch(
        setTransformationAction(configuration, {
          offset: getOffsetAfterResize(
            configuration.offset,
            getCurrentSizeOfConfiguration(configuration),
            scaleSize(configuration.size, nextScale),
            configuration.printArea
          ),
          scale: nextScale,
        })
      );
    } else {
      dispatch(
        setConfigurationTextStyle(configuration, {
          ...textStyle,
          fontSize: ptToMm(fontSize / (scale || 1)),
        })
      );
    }
  };

  const update = () => {
    const value = Math.max(Number.parseInt(inputValue, 10) || 0, minFontSize);
    setInputValue(value.toFixed(0));
    onChange(value);
  };

  return (
    <FontSizeInput
      inputValue={inputValue}
      onChange={(event) => setInputValue(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          update();
        }
      }}
      onBlur={update}
      onDecrease={() => onChange(Math.max(fontSize - 1, minFontSize))}
      onIncrease={() => onChange(fontSize + 1)}
    />
  );
};
